import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getApiKey, tvApi, type TvActivityItem } from '../services/birdbrain-api-client'

export type { TvActivityItem } from '../services/birdbrain-api-client'

interface UseActivityFeedOptions {
  /** API key from URL query param (fullscreen mode) */
  apiKey?: string | null
  /** Poll interval in ms. Default 25s. */
  pollInterval?: number
  /** Max items to request. Default 40. */
  limit?: number
}

/**
 * Polls the Taka TV live activity feed. Degrades gracefully to an empty list
 * if the endpoint is unavailable, so the dashboard never breaks because of it.
 */
export function useActivityFeed(options: UseActivityFeedOptions = {}) {
  const { apiKey, pollInterval = 25 * 1000, limit = 40 } = options
  const [items, setItems] = useState<TvActivityItem[]>([])
  const [error, setError] = useState<Error | null>(null)

  const effectiveApiKey = useMemo(() => apiKey || getApiKey(), [apiKey])
  const inFlight = useRef(false)

  const fetchFeed = useCallback(async () => {
    if (inFlight.current) return
    inFlight.current = true
    try {
      const res = effectiveApiKey
        ? await tvApi.getActivityFeedWithApiKey(effectiveApiKey, limit)
        : await tvApi.getActivityFeed(limit)
      setItems(res.items || [])
      setError(null)
    } catch (err) {
      // Non-fatal: keep whatever we had, surface the error for debugging only.
      setError(err instanceof Error ? err : new Error('Failed to fetch activity feed'))
    } finally {
      inFlight.current = false
    }
  }, [effectiveApiKey, limit])

  useEffect(() => {
    fetchFeed()
    const t = setInterval(fetchFeed, pollInterval)
    return () => clearInterval(t)
  }, [fetchFeed, pollInterval])

  return { items, error, refetch: fetchFeed }
}
